import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { FileText, Calculator, Download, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const currentMonth = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
  const [report, setReport] = useState(null);
  const [roiData, setRoiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [roiInputs, setRoiInputs] = useState({
    monthly_salary_cost: 5000,
    hours_saved_per_week: 10
  });

  const generateReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/monthly/${currentMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      alert('Error generating report');
    } finally {
      setLoading(false);
    }
  };

  const calculateROI = async () => {
    try {
      const response = await axios.get(
        `${API}/reports/roi-calculator?monthly_salary_cost=${roiInputs.monthly_salary_cost}&hours_saved_per_week=${roiInputs.hours_saved_per_week}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoiData(response.data);
    } catch (error) {
      alert('Error calculating ROI');
    }
  };

  const exportReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/export-pdf/${currentMonth}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report-${currentMonth}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error exporting report');
    }
  };

  const MONTHS = lang === 'nl'
    ? ['Januari','Februari','Maart','April','Mei','Juni','Juli','Augustus','September','Oktober','November','December']
    : ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const L = lang === 'nl' ? {
    back: 'Terug naar Portaal',
    title: 'Rapporten & Inzichten',
    monthlyReport: 'Maandrapport',
    generating: 'Genereren...',
    generate: 'Genereer',
    totalSessions: 'TOTAAL SESSIES',
    topTool: 'TOP TOOL',
    costSavings: 'KOSTENBESPARING',
    keyInsights: 'Belangrijkste Inzichten',
    exportReport: 'Exporteer Rapport',
    roiCalc: 'ROI Calculator',
    roiDesc: 'Bereken je rendement op investering',
    salaryCost: 'Maandelijks Salariskosten (€)',
    hoursSaved: 'Uren Bespaard per Week',
    calcRoi: 'Bereken ROI',
    roiPct: 'ROI PERCENTAGE',
    monthlySavings: 'Maandelijkse Besparing',
    annualSavings: 'Jaarlijkse Besparing',
    payback: 'Terugverdientijd',
    days: 'dagen',
  } : {
    back: 'Back to Portal',
    title: 'Reports & Insights',
    monthlyReport: 'Monthly Report',
    generating: 'Generating...',
    generate: 'Generate',
    totalSessions: 'TOTAL SESSIONS',
    topTool: 'TOP TOOL',
    costSavings: 'COST SAVINGS',
    keyInsights: 'Key Insights',
    exportReport: 'Export Report',
    roiCalc: 'ROI Calculator',
    roiDesc: 'Calculate your return on investment',
    salaryCost: 'Monthly Salary Cost (€)',
    hoursSaved: 'Hours Saved Per Week',
    calcRoi: 'Calculate ROI',
    roiPct: 'ROI PERCENTAGE',
    monthlySavings: 'Monthly Savings',
    annualSavings: 'Annual Savings',
    payback: 'Payback Period',
    days: 'days',
  };

  return (
    <div className="tool-page">
      <div className="tool-header">
        <h1 className="heading-1">{L.title}</h1>
      </div>

      <div className="reports-container">
        {/* Monthly Report */}
        <div className="report-section">
          <div className="report-section-header">
            <h2 className="heading-2">{L.monthlyReport}</h2>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="form-input"
                style={{ maxWidth: '160px' }}
                data-testid="report-month-select"
              >
                {(Array.isArray(MONTHS) ? MONTHS : []).map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="form-input"
                style={{ maxWidth: '100px' }}
                data-testid="report-year-select"
              >
                {[2024, 2025, 2026].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <button onClick={generateReport} disabled={loading} className="btn-primary">
                <FileText size={16} />
                {loading ? L.generating : L.generate}
              </button>
            </div>
          </div>

          {report && (
            <div className="report-content">
              <div className="report-metrics">
                <div className="metric-card">
                  <p className="caption">{L.totalSessions}</p>
                  <h2 className="heading-hero">{report.total_sessions}</h2>
                </div>
                <div className="metric-card">
                  <p className="caption">{L.topTool}</p>
                  <h3 className="heading-2">{report.top_tool?.charAt(0).toUpperCase() + report.top_tool?.slice(1)}</h3>
                </div>
                <div className="metric-card">
                  <p className="caption">{L.costSavings}</p>
                  <h2 className="heading-hero">€{report.estimated_cost_savings.toFixed(0)}</h2>
                </div>
              </div>

              <div className="insights-box">
                <h3 className="heading-3">{L.keyInsights}</h3>
                <ul className="insights-list">
                  {report.insights?.map((insight, idx) => (
                    <li key={idx} className="body-medium">{insight}</li>
                  ))}
                </ul>
              </div>

              <button onClick={exportReport} className="btn-secondary">
                <Download size={16} />
                {L.exportReport}
              </button>
            </div>
          )}
        </div>

        {/* ROI Calculator */}
        <div className="report-section">
          <h2 className="heading-2">{L.roiCalc}</h2>
          <p className="body-medium text-secondary">{L.roiDesc}</p>

          <div className="roi-inputs">
            <div className="form-group">
              <label className="body-small">{L.salaryCost}</label>
              <input
                type="number"
                value={roiInputs.monthly_salary_cost}
                onChange={(e) => setRoiInputs({ ...roiInputs, monthly_salary_cost: parseFloat(e.target.value) })}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="body-small">{L.hoursSaved}</label>
              <input
                type="number"
                value={roiInputs.hours_saved_per_week}
                onChange={(e) => setRoiInputs({ ...roiInputs, hours_saved_per_week: parseFloat(e.target.value) })}
                className="form-input"
              />
            </div>
            <button onClick={calculateROI} className="btn-primary">
              <Calculator size={16} />
              {L.calcRoi}
            </button>
          </div>

          {roiData && (
            <div className="roi-results">
              <div className="roi-card highlight">
                <TrendingUp size={32} />
                <div>
                  <p className="caption">{L.roiPct}</p>
                  <h1 className="heading-hero">{roiData.roi_percentage.toFixed(1)}%</h1>
                </div>
              </div>
              <div className="roi-metrics">
                <div className="roi-metric">
                  <p className="body-small">{L.monthlySavings}</p>
                  <p className="heading-3">€{roiData.net_monthly_savings.toFixed(2)}</p>
                </div>
                <div className="roi-metric">
                  <p className="body-small">{L.annualSavings}</p>
                  <p className="heading-3">€{roiData.annual_savings.toFixed(2)}</p>
                </div>
                <div className="roi-metric">
                  <p className="body-small">{L.payback}</p>
                  <p className="heading-3">{roiData.payback_period_days} {L.days}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
